package com.taocent.simple.compose.component.blockrichtext.internal.block.renderer

import androidx.compose.animation.core.tween
import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusTarget
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.zIndex
import com.taocent.simple.compose.component.blockrichtext.ExperimentalBlockRichTextApi
import com.taocent.simple.compose.component.blockrichtext.BlockState
import com.taocent.simple.compose.component.blockrichtext.TableBlock
import com.taocent.simple.compose.component.blockrichtext.*
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor
import com.taocent.simple.compose.component.richtext.core.LocalRichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextState
import kotlin.math.abs
import kotlin.math.roundToInt

/**
 * 表格内部拖动状态描述。
 * 由 `TableRenderer` 主 composable 与抽离出去的辅助组件
 * (`TableRowButton` / `TableColumnButton` 等)共享。
 */
internal data class DragState(
    val mode: DragMode,
    val dragOffset: Float = 0f,
    val targetIndex: Int = -1,
    val initialScrollValue: Int = 0,
    val wasSelected: Boolean = false
)

internal sealed class DragMode {
    data class Row(val fromIndex: Int, val rowHeightPx: Float) : DragMode()
    data class Column(val fromIndex: Int, val columnWidthPx: Float) : DragMode()
}

@Composable
@OptIn(ExperimentalBlockRichTextApi::class)
internal fun TableRenderer(
    block: TableBlock,
    blockState: BlockState,
    modifier: Modifier = Modifier
) {
    val config = LocalRichTextConfig.current
    val tableConfig = config.table
    val density = LocalDensity.current
    val cellMinHeightPx = with(density) { tableConfig.cellMinHeight.toPx() }
    var tableWidthPx by remember { mutableIntStateOf(0) }
    val tableWidthDp = with(density) { tableWidthPx.toDp() }
    val needScroll = block.columnCount > tableConfig.maxColumnsBeforeScroll
    val scrollState = rememberScrollState()
    // 触发行级左/右高亮或表格级左/右高亮时,自动把表格容器滚到首列/末列,
    // 防止列数 > maxColumnsBeforeScroll 时用户自己滑动后锚点高亮线被遮挡。
    // 滚动请求由 BlockState 的 navSelectTable / navSelectTableAfter /
    // navSelectRowBefore / navSelectRowAfter 写入,本表作为目标表时消费并清空。
    LaunchedEffect(blockState.pendingTableScrollRequest, block.id) {
        val req = blockState.pendingTableScrollRequest ?: return@LaunchedEffect
        if (req.tableId != block.id) return@LaunchedEffect
        // 等到容器布局完成(maxValue > 0)再滚,避免在 0 帧误调 scrollTo
        if (scrollState.maxValue <= 0) return@LaunchedEffect
        if (req.scrollToStart) {
            scrollState.scrollTo(0)
        } else {
            scrollState.scrollTo(scrollState.maxValue)
        }
        blockState.pendingTableScrollRequest = null
    }
    val isSelected = blockState.selectedTableBlockId == block.id
    val isNavSelected = blockState.navSelectedTableId == block.id
    // 锚点态进一步细分左/右两种视觉:Before -> 左高亮线;After -> 右高亮线。
    // 两者互斥,navSelected = isNavSelectedLeft || isNavSelectedRight。
    val navAnchorPosition = (blockState.editorCursor as? EditorCursor.BlockAnchor)
        ?.takeIf { it.blockId == block.id }
        ?.position
    val isNavSelectedLeft = isNavSelected &&
        navAnchorPosition != com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After
    val isNavSelectedRight = isNavSelected &&
        navAnchorPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After
    // 行锚点态:navRowAnchorTableId 命中本表 + navRowAnchorRowId 命中当前行时,
    // 该行的首列/末列 cell 分别画左/右高亮线。
    val isRowAnchorForThisTable = blockState.navRowAnchorTableId == block.id
    val rowAnchorRowId = blockState.navRowAnchorRowId
    val rowAnchorPosition = (blockState.editorCursor as? EditorCursor.RowAnchor)
        ?.takeIf { it.tableId == block.id }
        ?.position
    val menuButtonSize = tableConfig.menuButtonSize

    LaunchedEffect(isSelected) {
        if (isSelected) {
            kotlinx.coroutines.delay(tableConfig.selectionAutoClearDelayMs)
            blockState.clearTableSelection()
            blockState.navRestoreIfPending()
        }
    }

    // 行删除保护高亮自动清除:当 navRowDeleteGuardRowId 命中本表时,
    // 延迟后清除 guard,与表格选中保护一致。
    LaunchedEffect(blockState.navRowDeleteGuardRowId) {
        val guardRowId = blockState.navRowDeleteGuardRowId ?: return@LaunchedEffect
        kotlinx.coroutines.delay(tableConfig.selectionAutoClearDelayMs)
        // 过期检查:如果 guardRowId 仍是同一个(未被用户第二次按键覆盖),则清除
        if (blockState.navRowDeleteGuardRowId == guardRowId) {
            blockState.navRowDeleteGuardRowId = null
            blockState.navRowDeleteGuardTime = null
        }
    }

    val contentWidthDp = tableWidthDp - menuButtonSize * 2
    val columnWidthDp = if (needScroll) {
        val baseWidth = contentWidthDp / tableConfig.maxColumnsBeforeScroll
        baseWidth - tableConfig.columnScrollPadding
    } else {
        if (block.columnCount > 0) contentWidthDp / block.columnCount else contentWidthDp
    }
    val columnWidthPx = with(density) { columnWidthDp.toPx() }

    var focusedRowIndex by remember { mutableStateOf<Int?>(null) }
    var focusedColumnIndex by remember { mutableStateOf<Int?>(null) }
    var focusedRowId by remember { mutableStateOf<String?>(null) }
    var focusedCellId by remember { mutableStateOf<String?>(null) }
    var pendingFocusCellId by remember { mutableStateOf<Pair<String, String>?>(null) }
    var selectedRowIndex by remember { mutableStateOf<Int?>(null) }
    var selectedColumnIndex by remember { mutableStateOf<Int?>(null) }
    var showRowMenu by remember { mutableStateOf(false) }
    var showColumnMenu by remember { mutableStateOf(false) }

    var dragState by remember { mutableStateOf<DragState?>(null) }
    val rowHeightsPx = remember { mutableStateMapOf<Int, Float>() }
    val columnAnimStates = remember { mutableMapOf<String, androidx.compose.animation.core.Animatable<Float, *>>() }
    val rowAnimStates = remember { mutableMapOf<String, androidx.compose.animation.core.Animatable<Float, *>>() }

    val finishingAnimatable = remember { androidx.compose.animation.core.Animatable(0f) }
    var finishingTargetIndex by remember { mutableIntStateOf(-1) }
    var finishingIsRow by remember { mutableStateOf(false) }
    var finishingStartOffset by remember { mutableFloatStateOf(0f) }

    val cellStates = remember(block.id) {
        mutableMapOf<String, RichTextState>()
    }
    val cellSyncVersions = remember(block.id) {
        mutableMapOf<String, Int>()
    }
    val cellFocusRequesters = remember { mutableMapOf<Pair<String, String>, androidx.compose.ui.focus.FocusRequester>() }
    // 缓存每个 cell 最新一次的 TextLayoutResult,用于在 onPreviewKeyEvent 中精准判断
    // "光标是否在视觉首/末行",避免 ↑↓ 在多行 cell 中错误触发跨 block 跳转。
    // key 形如 "rowId__cellId"(表内唯一,不和 rowId/cellId 自身冲突)
    val cellTextLayouts = remember { mutableMapOf<String, TextLayoutResult>() }

    LaunchedEffect(block.rows, block.columnCount) {
        val validRows = block.rows.map { it.rowId }.toSet()
        val validCellIds = block.rows.flatMap { row -> row.cells.map { it.cellId } }.toSet()
        val validCellPairs = block.rows.flatMap { row -> row.cells.map { cell -> row.rowId to cell.cellId } }.toSet()
        val validCellStateKeys = block.rows.flatMap { row ->
            row.cells.map { cell -> "${block.id}_${row.rowId}_${cell.cellId}" }
        }.toSet()

        cellStates.keys.toList().forEach { key ->
            if (key !in validCellStateKeys) cellStates.remove(key)
        }
        cellSyncVersions.keys.toList().forEach { key ->
            if (key !in validCellStateKeys) cellSyncVersions.remove(key)
        }
        columnAnimStates.keys.toList().forEach { key ->
            if (key !in validCellIds) columnAnimStates.remove(key)
        }
        rowAnimStates.keys.toList().forEach { key ->
            if (key !in validRows) rowAnimStates.remove(key)
        }
        rowHeightsPx.keys.toList().forEach { index ->
            if (index !in block.rows.indices) rowHeightsPx.remove(index)
        }
        val validLayoutKeys = block.rows.flatMap { row ->
            row.cells.map { cell -> "${row.rowId}__${cell.cellId}" }
        }.toSet()
        cellTextLayouts.keys.toList().forEach { key ->
            if (key !in validLayoutKeys) cellTextLayouts.remove(key)
        }
        if (pendingFocusCellId != null && pendingFocusCellId !in validCellPairs) {
            pendingFocusCellId = null
        }
        val focusedPair = focusedRowId?.let { rowId -> focusedCellId?.let { cellId -> rowId to cellId } }
        if (focusedPair != null && focusedPair !in validCellPairs) {
            focusedRowIndex = null
            focusedColumnIndex = null
            focusedRowId = null
            focusedCellId = null
            if ((blockState.editorCursor as? EditorCursor.TableCell)?.tableId == block.id) {
                blockState.clearTableCellFocus()
            }
        }
        if (selectedRowIndex != null && selectedRowIndex !in block.rows.indices) {
            selectedRowIndex = null
        }
        if (selectedColumnIndex != null && selectedColumnIndex !in 0 until block.columnCount) {
            selectedColumnIndex = null
        }
    }

    LaunchedEffect(pendingFocusCellId) {
        val pending = pendingFocusCellId ?: return@LaunchedEffect
        cellFocusRequesters[pending]?.requestFocus()
        pendingFocusCellId = null
    }

    // 跨 block 进入表格 cell 的请求由 BlockStateNavigation.requestEnterTableFromAbove/Below 写入。
    // 仅当 tableId 命中本表格时才消费,避免多表格场景重复响应。
    LaunchedEffect(blockState.pendingEnterTableCell) {
        val req = blockState.pendingEnterTableCell ?: return@LaunchedEffect
        if (req.tableId != block.id) return@LaunchedEffect
        focusCellAtCaret(
            cellStates = cellStates,
            cellFocusRequesters = cellFocusRequesters,
            blockId = block.id,
            rowId = req.rowId,
            cellId = req.cellId,
            caretAtEnd = req.caretAtEnd
        )
        blockState.pendingEnterTableCell = null
    }

    val primaryColor = MaterialTheme.colorScheme.primary
    val edgeThresholdPx = with(density) { tableConfig.dragEdgeThreshold.toPx() }

    LaunchedEffect(dragState) {
        if (dragState == null) {
            columnAnimStates.values.forEach { it.snapTo(0f) }
            rowAnimStates.values.forEach { it.snapTo(0f) }
        }
    }

    LaunchedEffect(finishingTargetIndex) {
        if (finishingTargetIndex >= 0) {
            finishingAnimatable.snapTo(finishingStartOffset)
            finishingAnimatable.animateTo(0f, tween(tableConfig.dragFinishAnimationMs))
            finishingTargetIndex = -1
        }
    }

    LaunchedEffect(dragState) {
        val ds = dragState
        if (ds != null && ds.mode is DragMode.Column) {
            val colMode = ds.mode
            val containerWidthPx = tableWidthPx.toFloat() - with(density) { menuButtonSize.toPx() }
            while (true) {
                val colLeftEdge = colMode.fromIndex * colMode.columnWidthPx + ds.dragOffset
                val colRightEdge = colLeftEdge + colMode.columnWidthPx
                val visibleStart = scrollState.value.toFloat()
                val visibleEnd = visibleStart + containerWidthPx
                when {
                    colRightEdge > visibleEnd -> {
                        scrollState.scrollTo((scrollState.value + tableConfig.dragAutoScrollStepPx).coerceAtMost(scrollState.maxValue))
                    }
                    colLeftEdge < visibleStart -> {
                        scrollState.scrollTo((scrollState.value - tableConfig.dragAutoScrollStepPx).coerceAtLeast(0))
                    }
                }
                kotlinx.coroutines.delay(tableConfig.dragAutoScrollIntervalMs)
            }
        }
    }

    fun rowVisualOffsetAt(index: Int): Float {
        val row = block.rows.getOrNull(index) ?: return 0f
        val ds = dragState
        val rowMode = ds?.mode as? DragMode.Row
        return when {
            rowMode != null && rowMode.fromIndex == index -> ds.dragOffset
            rowMode != null && rowMode.fromIndex < ds.targetIndex && index in (rowMode.fromIndex + 1)..ds.targetIndex ->
                rowAnimStates[row.rowId]?.value ?: -rowMode.rowHeightPx
            rowMode != null && rowMode.fromIndex > ds.targetIndex && index in ds.targetIndex until rowMode.fromIndex ->
                rowAnimStates[row.rowId]?.value ?: rowMode.rowHeightPx
            finishingIsRow && finishingTargetIndex == index && finishingTargetIndex >= 0 -> finishingAnimatable.value
            else -> 0f
        }
    }

    fun columnVisualOffsetAt(index: Int): Float {
        val firstRowCellId = block.rows.firstOrNull()?.cells?.getOrNull(index)?.cellId
        val ds = dragState
        val colMode = ds?.mode as? DragMode.Column
        val scrollDelta = ds
            ?.takeIf { colMode != null && colMode.fromIndex == index }
            ?.let { (scrollState.value - it.initialScrollValue).toFloat() }
            ?: 0f
        return when {
            colMode != null && colMode.fromIndex == index -> ds.dragOffset + scrollDelta
            colMode != null && colMode.fromIndex < ds.targetIndex && index in (colMode.fromIndex + 1)..ds.targetIndex ->
                firstRowCellId?.let { columnAnimStates[it]?.value } ?: -colMode.columnWidthPx
            colMode != null && colMode.fromIndex > ds.targetIndex && index in ds.targetIndex until colMode.fromIndex ->
                firstRowCellId?.let { columnAnimStates[it]?.value } ?: colMode.columnWidthPx
            !finishingIsRow && finishingTargetIndex == index && finishingTargetIndex >= 0 -> finishingAnimatable.value
            else -> 0f
        }
    }

    val borderSeparationThresholdPx = 0.5f

    Column(
        modifier = modifier
            .fillMaxWidth()
            .focusTarget()
            .onFocusChanged { state ->
                if (!state.hasFocus) {
                    focusedRowIndex = null
                    focusedColumnIndex = null
                    focusedRowId = null
                    focusedCellId = null
                    selectedRowIndex = null
                    selectedColumnIndex = null
                    showRowMenu = false
                    showColumnMenu = false
                }
            }
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                val rowId = focusedRowId ?: return@onPreviewKeyEvent false
                val cellId = focusedCellId ?: return@onPreviewKeyEvent false
                val rowIndex = block.rows.indexOfFirst { it.rowId == rowId }
                if (rowIndex < 0) return@onPreviewKeyEvent false
                val row = block.rows[rowIndex]
                val colIndex = row.cells.indexOfFirst { it.cellId == cellId }
                if (colIndex < 0) return@onPreviewKeyEvent false
                val cellKey = "${block.id}_${rowId}_${cellId}"
                val cellState = cellStates[cellKey] ?: return@onPreviewKeyEvent false

                // IME composition 期间不拦截方向键(把候选窗 ↑↓ 选词留给 IME)
                if (cellState.textFieldValue.composition != null) return@onPreviewKeyEvent false

                // 计算光标视觉行信息(用于 ↑↓ 判定是否真在 cell 视觉首/末行)。
                // 没有 layout 缓存时回退为单行假设,行为与之前一致。
                val layoutKey = "${rowId}__${cellId}"
                val layout = cellTextLayouts[layoutKey]
                val caretOffset = cellState.textFieldValue.selection.start
                val caretLine = layout?.let {
                    val safe = caretOffset.coerceIn(0, it.layoutInput.text.length)
                    runCatching { it.getLineForOffset(safe) }.getOrNull()
                }
                val totalLines = layout?.lineCount ?: 1
                val caretAtVisualFirstLine = caretLine == null || caretLine == 0
                val caretAtVisualLastLine = caretLine == null || caretLine == totalLines - 1
                val caretAtStart = caretOffset == 0
                val caretAtEnd = caretOffset == (layout?.layoutInput?.text?.length ?: cellState.textFieldValue.text.length)

                when (event.key) {
                    Key.Tab -> {
                        // Tab / Shift+Tab cell 间跳转(电子表格语义)
                        if (event.isShiftPressed) {
                            // Shift+Tab:先尝试上一格,再 (0,0) 时跳出表格
                            when {
                                colIndex > 0 -> {
                                    val targetCell = row.cells[colIndex - 1]
                                    focusCellAtCaret(
                                        cellStates = cellStates,
                                        cellFocusRequesters = cellFocusRequesters,
                                        blockId = block.id,
                                        rowId = row.rowId,
                                        cellId = targetCell.cellId,
                                        caretAtEnd = true
                                    )
                                    true
                                }
                                rowIndex > 0 -> {
                                    val targetRow = block.rows[rowIndex - 1]
                                    val targetCell = targetRow.cells.last()
                                    focusCellAtCaret(
                                        cellStates = cellStates,
                                        cellFocusRequesters = cellFocusRequesters,
                                        blockId = block.id,
                                        rowId = targetRow.rowId,
                                        cellId = targetCell.cellId,
                                        caretAtEnd = true
                                    )
                                    true
                                }
                                else -> blockState.shiftTabExitTable(block.id)
                            }
                        } else {
                            // Tab:先尝试下一格,再行末时换行;最末格时走 tabExitOrAppendRow
                            when {
                                colIndex < row.cells.lastIndex -> {
                                    val targetCell = row.cells[colIndex + 1]
                                    focusCellAtCaret(
                                        cellStates = cellStates,
                                        cellFocusRequesters = cellFocusRequesters,
                                        blockId = block.id,
                                        rowId = row.rowId,
                                        cellId = targetCell.cellId,
                                        caretAtEnd = true
                                    )
                                    true
                                }
                                rowIndex < block.rows.lastIndex -> {
                                    val targetRow = block.rows[rowIndex + 1]
                                    val targetCell = targetRow.cells.first()
                                    focusCellAtCaret(
                                        cellStates = cellStates,
                                        cellFocusRequesters = cellFocusRequesters,
                                        blockId = block.id,
                                        rowId = targetRow.rowId,
                                        cellId = targetCell.cellId,
                                        caretAtEnd = true
                                    )
                                    true
                                }
                                else -> blockState.tabExitOrAppendRow(block.id)
                            }
                        }
                    }
                    Key.DirectionUp -> {
                        // Shift+↑(选区扩展):始终把事件交还 BasicTextField,
                        // 由其在 cell 内部按视觉行扩展 selection,不跳出 cell、不进锚点态。
                        // 这样"视觉首行任意位置 Shift+↑"会自然把选区延伸到 cell 开头,
                        // 而不是错误地跨 block 跳转。
                        if (event.isShiftPressed) return@onPreviewKeyEvent false
                        // 视觉行 > 0:让 BasicTextField 处理多行 cell 内的视觉行迁移,
                        // 不拦截事件;只在视觉首行且首位才走 cell 间 / 跨 block 跳转。
                        if (!caretAtVisualFirstLine || !caretAtStart) return@onPreviewKeyEvent false
                        if (rowIndex > 0) {
                            val targetRow = block.rows[rowIndex - 1]
                            val targetCol = colIndex.coerceAtMost(targetRow.cells.lastIndex)
                            val targetCell = targetRow.cells[targetCol]
                            focusCellAtCaret(
                                cellStates = cellStates,
                                cellFocusRequesters = cellFocusRequesters,
                                blockId = block.id,
                                rowId = targetRow.rowId,
                                cellId = targetCell.cellId,
                                caretAtEnd = true
                            )
                            true
                        } else {
                            // 默认 ↑:直接进上方相邻表格末行同列 cell;
                            // (Shift+↑ 已在前面 return false,不会走到这里)
                            blockState.exitTableUpward(
                                block.id,
                                useAnchor = false,
                                preferredColumn = colIndex
                            )
                        }
                    }
                    Key.DirectionDown -> {
                        // Shift+↓(选区扩展):同上,让 BasicTextField 在 cell 内扩展选区
                        if (event.isShiftPressed) return@onPreviewKeyEvent false
                        // 只在视觉末行且末位才走 cell 间 / 跨 block 跳转。
                        if (!caretAtVisualLastLine || !caretAtEnd) return@onPreviewKeyEvent false
                        if (rowIndex < block.rows.lastIndex) {
                            val targetRow = block.rows[rowIndex + 1]
                            val targetCol = colIndex.coerceAtMost(targetRow.cells.lastIndex)
                            val targetCell = targetRow.cells[targetCol]
                            focusCellAtCaret(
                                cellStates = cellStates,
                                cellFocusRequesters = cellFocusRequesters,
                                blockId = block.id,
                                rowId = targetRow.rowId,
                                cellId = targetCell.cellId,
                                caretAtEnd = false
                            )
                            true
                        } else {
                            blockState.exitTableDownward(
                                block.id,
                                useAnchor = false,
                                preferredColumn = colIndex
                            )
                        }
                    }
                    Key.DirectionLeft -> {
                        val sel = cellState.textFieldValue.selection
                        if (!sel.collapsed || sel.min != 0) return@onPreviewKeyEvent false
                        // 行首格首位 Shift+←:进入该行的行左高亮(Before)
                        if (event.isShiftPressed && colIndex == 0 && caretAtVisualFirstLine) {
                            blockState.navSelectRowBefore(block.id, row.rowId)
                            return@onPreviewKeyEvent true
                        }
                        when {
                            colIndex > 0 -> {
                                val targetCell = row.cells[colIndex - 1]
                                focusCellAtCaret(
                                    cellStates = cellStates,
                                    cellFocusRequesters = cellFocusRequesters,
                                    blockId = block.id,
                                    rowId = row.rowId,
                                    cellId = targetCell.cellId,
                                    caretAtEnd = true
                                )
                                true
                            }
                            rowIndex > 0 -> {
                                // 非首行的首列 ←:跳到上一行的末列(电子表格语义)
                                val targetRow = block.rows[rowIndex - 1]
                                val targetCell = targetRow.cells.last()
                                focusCellAtCaret(
                                    cellStates = cellStates,
                                    cellFocusRequesters = cellFocusRequesters,
                                    blockId = block.id,
                                    rowId = targetRow.rowId,
                                    cellId = targetCell.cellId,
                                    caretAtEnd = true
                                )
                                true
                            }
                            else -> {
                                // (row=0, col=0) ←:走"前一格"语义,跨表落点 = 上表末行末列(尾格);
                                // 与 ↑ 的"行迁移、同列"语义解耦,避免被误当作上方向键。
                                blockState.exitTableLeftToPrevCell(block.id)
                            }
                        }
                    }
                    Key.DirectionRight -> {
                        val sel = cellState.textFieldValue.selection
                        val textLen = cellState.textFieldValue.text.length
                        if (!sel.collapsed || sel.min < textLen) return@onPreviewKeyEvent false
                        // 行末格末位 Shift+→:进入该行的行右高亮(After)
                        if (event.isShiftPressed && colIndex == row.cells.lastIndex && caretAtVisualLastLine) {
                            blockState.navSelectRowAfter(block.id, row.rowId)
                            return@onPreviewKeyEvent true
                        }
                        when {
                            colIndex < row.cells.lastIndex -> {
                                val targetCell = row.cells[colIndex + 1]
                                focusCellAtCaret(
                                    cellStates = cellStates,
                                    cellFocusRequesters = cellFocusRequesters,
                                    blockId = block.id,
                                    rowId = row.rowId,
                                    cellId = targetCell.cellId,
                                    caretAtEnd = false
                                )
                                true
                            }
                            rowIndex < block.rows.lastIndex -> {
                                // 非末行的末列 →:跳到下一行的首列
                                val targetRow = block.rows[rowIndex + 1]
                                val targetCell = targetRow.cells.first()
                                focusCellAtCaret(
                                    cellStates = cellStates,
                                    cellFocusRequesters = cellFocusRequesters,
                                    blockId = block.id,
                                    rowId = targetRow.rowId,
                                    cellId = targetCell.cellId,
                                    caretAtEnd = false
                                )
                                true
                            }
                            else -> {
                                // (row=last, col=last) →:跳出到下方 block 头部
                                blockState.exitTableDownward(block.id)
                            }
                        }
                    }
                    else -> false
                }
            }
            .onSizeChanged { tableWidthPx = it.width }
            .padding(end = menuButtonSize)
    ) {
        Row {
            // 行菜单按钮列(固定在滚动外面)
            Column {
                Spacer(modifier = Modifier.height(menuButtonSize))
                block.rows.forEachIndexed { rowIndex, row ->
                    val rowDs = dragState
                    val rowMode = (rowDs?.mode as? DragMode.Row)
                    val isDraggingRow = rowMode != null && rowMode.fromIndex == rowIndex

                    val rowOffset = if (rowDs != null) {
                        if (isDraggingRow) {
                            rowDs.dragOffset
                        } else {
                            val rTarget = when {
                                rowMode != null && rowMode.fromIndex < rowDs.targetIndex &&
                                    rowIndex in (rowMode.fromIndex + 1)..rowDs.targetIndex -> -rowMode.rowHeightPx
                                rowMode != null && rowMode.fromIndex > rowDs.targetIndex &&
                                    rowIndex in rowDs.targetIndex until rowMode.fromIndex -> rowMode.rowHeightPx
                                else -> 0f
                            }
                            val rAnim = rowAnimStates.getOrPut(row.rowId) {
                                androidx.compose.animation.core.Animatable(0f)
                            }
                            LaunchedEffect(rTarget) {
                                rAnim.animateTo(rTarget, tween(tableConfig.dragPlaceholderAnimationMs))
                            }
                            rAnim.value
                        }
                    } else if (finishingIsRow && finishingTargetIndex == rowIndex && finishingTargetIndex >= 0) {
                        finishingAnimatable.value
                    } else {
                        0f
                    }

                    val isRowSelected = selectedRowIndex == rowIndex
                    val dragGestureModifier = Modifier.pointerInput(rowIndex, block.rows.size) {
                            detectDragGesturesAfterLongPress(
                                onDragStart = {
                                    val rowHeight = rowHeightsPx[rowIndex] ?: cellMinHeightPx
                                    dragState = DragState(
                                        mode = DragMode.Row(fromIndex = rowIndex, rowHeightPx = rowHeight),
                                        targetIndex = rowIndex,
                                        wasSelected = selectedRowIndex == rowIndex
                                    )
                                },
                                onDrag = { change, dragAmount ->
                                    change.consume()
                                    val current = dragState ?: return@detectDragGesturesAfterLongPress
                                    val newOffset = current.dragOffset + dragAmount.y
                                    val mode = current.mode
                                    if (mode is DragMode.Row) {
                                            val targetIndex = if (newOffset > 0) {
                                                var acc = 0f
                                                var target = mode.fromIndex
                                                for (i in (mode.fromIndex + 1)..block.rows.lastIndex) {
                                                    acc += rowHeightsPx[i] ?: cellMinHeightPx
                                                    if (newOffset >= acc - (rowHeightsPx[i] ?: cellMinHeightPx) / 2f) {
                                                        target = i
                                                    } else break
                                                }
                                                target
                                            } else {
                                                var acc = 0f
                                                var target = mode.fromIndex
                                                for (i in (mode.fromIndex - 1) downTo 0) {
                                                    acc += rowHeightsPx[i] ?: cellMinHeightPx
                                                    if (-newOffset >= acc - (rowHeightsPx[i] ?: cellMinHeightPx) / 2f) {
                                                        target = i
                                                    } else break
                                                }
                                                target
                                            }
                                            dragState = current.copy(dragOffset = newOffset, targetIndex = targetIndex)
                                        }
                                    },
                                    onDragEnd = {
                                        val current = dragState
                                        if (current != null && current.mode is DragMode.Row) {
                                            val from = current.mode.fromIndex
                                            val to = current.targetIndex
                                            if (to >= 0 && from != to) {
                                                    val focusedRowIdBefore = focusedRowId
                                                    val focusedCellIdBefore = focusedCellId
                                                    blockState.moveTableRow(block.id, from, to)
                                                    val focusedRow = focusedRowIndex
                                                    if (focusedRow != null) {
                                                        val newFocusedRow = when {
                                                            focusedRow == from -> to
                                                            from < focusedRow && to >= focusedRow -> focusedRow - 1
                                                            from > focusedRow && to <= focusedRow -> focusedRow + 1
                                                            else -> focusedRow
                                                        }
                                                        focusedRowIndex = newFocusedRow
                                                        if (focusedRowIdBefore != null && focusedCellIdBefore != null) {
                                                            pendingFocusCellId = Pair(focusedRowIdBefore, focusedCellIdBefore)
                                                        }
                                                    }
                                                if (current.wasSelected) {
                                                    selectedRowIndex = to
                                                    finishingStartOffset = current.dragOffset - (to - from) * current.mode.rowHeightPx
                                                    finishingTargetIndex = to
                                                    finishingIsRow = true
                                                }
                                            }
                                        }
                                        dragState = null
                                    },
                                    onDragCancel = { dragState = null }
                                )
                            }

                        val rowHeightDp = with(density) { (rowHeightsPx[rowIndex] ?: 0f).toDp() }
                        val isRowLifted = isDraggingRow || (finishingIsRow && finishingTargetIndex == rowIndex && finishingTargetIndex >= 0)
                        val showRowButton = focusedRowIndex == rowIndex || selectedRowIndex == rowIndex

                        TableRowButton(
                            menuButtonSize = menuButtonSize,
                            tableConfig = tableConfig,
                            rowOffset = rowOffset,
                            rowHeightDp = rowHeightDp,
                            isRowLifted = isRowLifted,
                            showRowButton = showRowButton,
                            isSelectedRow = selectedRowIndex == rowIndex,
                            showRowMenu = showRowMenu,
                            dragGestureModifier = if (showRowButton) dragGestureModifier else Modifier,
                            onRowClick = {
                                if (selectedRowIndex == rowIndex) {
                                    showRowMenu = true
                                } else {
                                    selectedRowIndex = rowIndex
                                    selectedColumnIndex = null
                                }
                            },
                            onInsertAbove = {
                                blockState.insertTableRow(block.id, rowIndex, insertAbove = true)
                                showRowMenu = false
                                selectedRowIndex = null
                            },
                            onInsertBelow = {
                                blockState.insertTableRow(block.id, rowIndex, insertAbove = false)
                                showRowMenu = false
                                selectedRowIndex = null
                            },
                            onInsertTableAbove = {
                                blockState.insertTableAbove(block.id, block.rows.size, block.columnCount)
                                showRowMenu = false
                                selectedRowIndex = null
                            },
                            onDeleteRow = {
                                blockState.deleteTableRow(block.id, rowIndex)
                                showRowMenu = false
                                selectedRowIndex = null
                            },
                            onDeleteTable = {
                                // 行菜单里点"删除当前表格":直接根据当前 table 的 id
                                // 调 `deleteTable`,不依赖 `selectedTableBlockId` 选中态
                                // (row 选中态是 TableRenderer 局部的 selectedRowIndex,
                                //  不会同步到 BlockState.selectedTableBlockId)。
                                blockState.deleteTable(block.id)
                                showRowMenu = false
                                selectedRowIndex = null
                            },
                            onDismissMenu = { showRowMenu = false },
                        )
                }
            }

            // 可滚动区域:列按钮 + 单元格
            Row(
                modifier = if (needScroll) {
                    Modifier.horizontalScroll(scrollState)
                } else {
                    Modifier
                }
            ) {
                Column {
                    // 列菜单按钮行
                    Row {
                        block.rows.firstOrNull()?.cells?.forEachIndexed { columnIndex, cell ->
                            val ds = dragState
                            val colMode = (ds?.mode as? DragMode.Column)
                            val isDraggingCol = colMode != null && colMode.fromIndex == columnIndex

                            val scrollDelta = if (isDraggingCol) {
                                (scrollState.value - ds.initialScrollValue).toFloat()
                            } else 0f

                            val colOffset = if (ds != null) {
                                if (isDraggingCol) {
                                    ds.dragOffset + scrollDelta
                                } else {
                                    val target = when {
                                        colMode != null && colMode.fromIndex < ds.targetIndex &&
                                            columnIndex in (colMode.fromIndex + 1)..ds.targetIndex -> -colMode.columnWidthPx
                                        colMode != null && colMode.fromIndex > ds.targetIndex &&
                                            columnIndex in ds.targetIndex until colMode.fromIndex -> colMode.columnWidthPx
                                        else -> 0f
                                    }
                                    val anim = columnAnimStates.getOrPut(cell.cellId) {
                                        androidx.compose.animation.core.Animatable(0f)
                                    }
                                    LaunchedEffect(target) {
                                        anim.animateTo(target, tween(tableConfig.dragPlaceholderAnimationMs))
                                    }
                                    anim.value
                                }
                            } else if (!finishingIsRow && finishingTargetIndex == columnIndex && finishingTargetIndex >= 0) {
                                finishingAnimatable.value
                            } else {
                                0f
                            }

                            val colDragGestureModifier = Modifier.pointerInput(columnIndex, block.columnCount) {
                                    detectDragGesturesAfterLongPress(
                                        onDragStart = {
                                            dragState = DragState(
                                                mode = DragMode.Column(
                                                    fromIndex = columnIndex,
                                                    columnWidthPx = columnWidthPx
                                                ),
                                                targetIndex = columnIndex,
                                                initialScrollValue = scrollState.value,
                                                wasSelected = selectedColumnIndex == columnIndex
                                            )
                                        },
                                        onDrag = { change, dragAmount ->
                                            change.consume()
                                            val current = dragState ?: return@detectDragGesturesAfterLongPress
                                            val newOffset = current.dragOffset + dragAmount.x
                                            val mode = current.mode
                                            if (mode is DragMode.Column) {
                                                val effectiveOffset = newOffset +
                                                    (scrollState.value - current.initialScrollValue).toFloat()
                                                val targetIndex = if (effectiveOffset > 0) {
                                                    val steps = ((effectiveOffset + mode.columnWidthPx / 2) / mode.columnWidthPx).toInt()
                                                    (mode.fromIndex + steps).coerceAtMost(block.columnCount - 1)
                                                } else {
                                                    val steps = ((-effectiveOffset + mode.columnWidthPx / 2) / mode.columnWidthPx).toInt()
                                                    (mode.fromIndex - steps).coerceAtLeast(0)
                                                }
                                                dragState = current.copy(dragOffset = newOffset, targetIndex = targetIndex)
                                            }
                                        },
                                        onDragEnd = {
                                            val current = dragState
                                            if (current != null && current.mode is DragMode.Column) {
                                                val mode = current.mode
                                                val effectiveOffset = current.dragOffset +
                                                    (scrollState.value - current.initialScrollValue).toFloat()
                                                val finalTarget = if (effectiveOffset > 0) {
                                                    val steps = ((effectiveOffset + mode.columnWidthPx / 2) / mode.columnWidthPx).toInt()
                                                    (mode.fromIndex + steps).coerceAtMost(block.columnCount - 1)
                                                } else {
                                                    val steps = ((-effectiveOffset + mode.columnWidthPx / 2) / mode.columnWidthPx).toInt()
                                                    (mode.fromIndex - steps).coerceAtLeast(0)
                                                }
                                                val from = mode.fromIndex
                                                if (from != finalTarget) {
                                                    val focusedRowIdBefore = focusedRowId
                                                    val focusedCellIdBefore = focusedCellId
                                                    blockState.moveTableColumn(block.id, from, finalTarget)
                                                    val focusedCol = focusedColumnIndex
                                                    if (focusedCol != null) {
                                                        val newFocusedCol = when {
                                                            focusedCol == from -> finalTarget
                                                            from < focusedCol && finalTarget >= focusedCol -> focusedCol - 1
                                                            from > focusedCol && finalTarget <= focusedCol -> focusedCol + 1
                                                            else -> focusedCol
                                                        }
                                                        focusedColumnIndex = newFocusedCol
                                                        if (focusedRowIdBefore != null && focusedCellIdBefore != null) {
                                                            pendingFocusCellId = Pair(focusedRowIdBefore, focusedCellIdBefore)
                                                        }
                                                    }
                                                    if (current.wasSelected) {
                                                        selectedColumnIndex = finalTarget
                                                        finishingStartOffset = effectiveOffset - (finalTarget - from) * mode.columnWidthPx
                                                        finishingTargetIndex = finalTarget
                                                        finishingIsRow = false
                                                    }
                                                }
                                            }
                                            dragState = null
                                        },
                                        onDragCancel = { dragState = null }
                                    )
                                }

                            val isColLifted = isDraggingCol || (!finishingIsRow && finishingTargetIndex == columnIndex && finishingTargetIndex >= 0)
                            val showColumnButton = focusedColumnIndex == columnIndex || selectedColumnIndex == columnIndex

                            TableColumnButton(
                                menuButtonSize = menuButtonSize,
                                columnWidthDp = columnWidthDp,
                                colOffset = colOffset,
                                isColLifted = isColLifted,
                                showColumnButton = showColumnButton,
                                isSelectedCol = selectedColumnIndex == columnIndex,
                                showColumnMenu = showColumnMenu,
                                colDragGestureModifier = if (showColumnButton) colDragGestureModifier else Modifier,
                                onColumnClick = {
                                    if (selectedColumnIndex == columnIndex) {
                                        showColumnMenu = true
                                    } else {
                                        selectedColumnIndex = columnIndex
                                        selectedRowIndex = null
                                    }
                                },
                                onInsertLeft = {
                                    val restoreRowId = focusedRowId
                                    val restoreCellId = focusedCellId
                                    val oldFocusedCol = focusedColumnIndex
                                    blockState.insertTableColumn(block.id, columnIndex, insertLeft = true)
                                    if (restoreRowId != null && restoreCellId != null) {
                                        pendingFocusCellId = Pair(restoreRowId, restoreCellId)
                                    }
                                    // 新列插入到 columnIndex,位于 >= columnIndex 的 cell 列号右移 1
                                    // requestFocus() 不会再次触发 onFocusChanged(isFocused=true),
                                    // 因此必须手动同步 focusedColumnIndex,让列级按钮跟随光标新位置
                                    if (oldFocusedCol != null && oldFocusedCol >= columnIndex) {
                                        focusedColumnIndex = oldFocusedCol + 1
                                    }
                                    showColumnMenu = false
                                    selectedColumnIndex = null
                                },
                                onInsertRight = {
                                    val restoreRowId = focusedRowId
                                    val restoreCellId = focusedCellId
                                    val oldFocusedCol = focusedColumnIndex
                                    blockState.insertTableColumn(block.id, columnIndex, insertLeft = false)
                                    if (restoreRowId != null && restoreCellId != null) {
                                        pendingFocusCellId = Pair(restoreRowId, restoreCellId)
                                    }
                                    // 新列插入到 columnIndex + 1,位于 > columnIndex 的 cell 列号右移 1
                                    if (oldFocusedCol != null && oldFocusedCol > columnIndex) {
                                        focusedColumnIndex = oldFocusedCol + 1
                                    }
                                    showColumnMenu = false
                                    selectedColumnIndex = null
                                },
                                onDeleteColumn = {
                                    blockState.deleteTableColumn(block.id, columnIndex)
                                    showColumnMenu = false
                                    selectedColumnIndex = null
                                },
                                onDismissMenu = { showColumnMenu = false },
                            )
                        }
                    }

                    // 表格行(只有单元格,行按钮在外面)
                    block.rows.forEachIndexed { rowIndex, row ->
                        val tblRowDs = dragState
                        val tblRowMode = (tblRowDs?.mode as? DragMode.Row)
                        val isTblDraggingRow = tblRowMode != null && tblRowMode.fromIndex == rowIndex

                        val tblRowOffset = if (tblRowDs != null) {
                            if (isTblDraggingRow) {
                                tblRowDs.dragOffset
                            } else {
                                val tblTarget = when {
                                    tblRowMode != null && tblRowMode.fromIndex < tblRowDs.targetIndex &&
                                        rowIndex in (tblRowMode.fromIndex + 1)..tblRowDs.targetIndex -> -tblRowMode.rowHeightPx
                                    tblRowMode != null && tblRowMode.fromIndex > tblRowDs.targetIndex &&
                                        rowIndex in tblRowDs.targetIndex until tblRowMode.fromIndex -> tblRowMode.rowHeightPx
                                    else -> 0f
                                }
                                val tblAnim = rowAnimStates.getOrPut(row.rowId) {
                                    androidx.compose.animation.core.Animatable(0f)
                                }
                                LaunchedEffect(tblTarget) {
                                    tblAnim.animateTo(tblTarget, tween(tableConfig.dragPlaceholderAnimationMs))
                                }
                                tblAnim.value
                            }
                        } else if (finishingIsRow && finishingTargetIndex == rowIndex && finishingTargetIndex >= 0) {
                            finishingAnimatable.value
                        } else {
                            0f
                        }

                        val isSelectedRow = selectedRowIndex == rowIndex
                        val isTblRowLifted = isTblDraggingRow || (finishingIsRow && finishingTargetIndex == rowIndex && finishingTargetIndex >= 0)

                        val rowModifier = Modifier
                            .zIndex(if (isTblRowLifted) 1f else 0f)
                            .height(IntrinsicSize.Min)
                            .offset { IntOffset(0, tblRowOffset.roundToInt()) }
                        Row(modifier = rowModifier.onSizeChanged { rowHeightsPx[rowIndex] = it.height.toFloat() }) {
                            row.cells.forEachIndexed { cellIndex, cell ->
                                key(cell.cellId) {
                                    // 计算单元格列偏移(拖拽相关)
                                    val cellDs = dragState
                                    val cellColMode = (cellDs?.mode as? DragMode.Column)
                                    val isCellDraggingCol = cellColMode != null && cellColMode.fromIndex == cellIndex
                                    val cellScrollDelta = cellDs
                                        ?.takeIf { isCellDraggingCol }
                                        ?.let { (scrollState.value - it.initialScrollValue).toFloat() }
                                        ?: 0f
                                    val cellColOffset = if (cellDs != null) {
                                        if (isCellDraggingCol) {
                                            cellDs.dragOffset + cellScrollDelta
                                        } else {
                                            val cellTarget = when {
                                                cellColMode != null && cellColMode.fromIndex < cellDs.targetIndex &&
                                                    cellIndex in (cellColMode.fromIndex + 1)..cellDs.targetIndex -> -cellColMode.columnWidthPx
                                                cellColMode != null && cellColMode.fromIndex > cellDs.targetIndex &&
                                                    cellIndex in cellDs.targetIndex until cellColMode.fromIndex -> cellColMode.columnWidthPx
                                                else -> 0f
                                            }
                                            val cellAnim = columnAnimStates.getOrPut(cell.cellId) {
                                                androidx.compose.animation.core.Animatable(0f)
                                            }
                                            LaunchedEffect(cellTarget) {
                                                cellAnim.animateTo(cellTarget, tween(tableConfig.dragPlaceholderAnimationMs))
                                            }
                                            cellAnim.value
                                        }
                                    } else if (!finishingIsRow && finishingTargetIndex == cellIndex && finishingTargetIndex >= 0) {
                                        finishingAnimatable.value
                                    } else {
                                        0f
                                    }

                                    val isCellLifted = isCellDraggingCol || (!finishingIsRow && finishingTargetIndex == cellIndex && finishingTargetIndex >= 0)
                                    val drawRightBorder = cellIndex == block.columnCount - 1 ||
                                        abs(cellColOffset - columnVisualOffsetAt(cellIndex + 1)) > borderSeparationThresholdPx
                                    val drawBottomBorder = rowIndex == block.rows.lastIndex ||
                                        abs(tblRowOffset - rowVisualOffsetAt(rowIndex + 1)) > borderSeparationThresholdPx

                                    TableCell(
                                        cell = cell,
                                        rowId = row.rowId,
                                        rowIndex = rowIndex,
                                        cellIndex = cellIndex,
                                        columnWidthDp = columnWidthDp,
                                        blockId = block.id,
                                        blockRows = block.rows.size,
                                        blockCols = block.columnCount,
                                        blockState = blockState,
                                        tableConfig = tableConfig,
                                        primaryColor = primaryColor,
                                        isTableSelected = isSelected,
                                        isNavSelectedLeft = isNavSelectedLeft,
                                        isNavSelectedRight = isNavSelectedRight,
                                        isRowAnchorLeft = isRowAnchorForThisTable &&
                                            row.rowId == rowAnchorRowId &&
                                            rowAnchorPosition != com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After,
                                        isRowAnchorRight = isRowAnchorForThisTable &&
                                            row.rowId == rowAnchorRowId &&
                                            rowAnchorPosition == com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition.After,
                                        isSelectedRow = isSelectedRow,
                                        isSelectedCol = selectedColumnIndex == cellIndex,
                                        drawRightBorder = drawRightBorder,
                                        drawBottomBorder = drawBottomBorder,
                                        cellColOffset = cellColOffset,
                                        isCellLifted = isCellLifted,
                                        cellStates = cellStates,
                                        cellSyncVersions = cellSyncVersions,
                                        cellFocusRequesters = cellFocusRequesters,
                                        onFocus = { ri, ci, rid, cid, cs, fr ->
                                            focusedRowIndex = ri
                                            focusedColumnIndex = ci
                                            focusedRowId = rid
                                            focusedCellId = cid
                                            selectedRowIndex = null
                                            selectedColumnIndex = null
                                            showRowMenu = false
                                            showColumnMenu = false
                                            blockState.navClearSelection()
                                            // 标记"用户曾主动 focus 过任意 RichTextTextField",与文本块
                                            // TextBlockItem.onFocusChanged 保持一致:供 emoji 插入/dialog 关闭
                                            // 路径判断"是否需要恢复焦点(及软键盘)"。
                                            blockState.hasUserFocusedAnyField = true
                                            blockState.focusTableCell(block.id, rid, cid, cs, fr)
                                        },
                                        onClearSelection = {
                                            selectedRowIndex = null
                                            selectedColumnIndex = null
                                            showRowMenu = false
                                            showColumnMenu = false
                                        },
                                        onTextLayout = { rid, cid, layout ->
                                            cellTextLayouts["${rid}__${cid}"] = layout
                                        },
                                    )
                                }
                            }
                        }

                    }
                }
            }
        }
    }
}
